import axios from "axios";
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { apiKey } from "../../config/config";
import { languageHelper } from "../../config/helperFunctions";

function ThankYou({ logo, generalSetting }) {
  const [orderDetails, setOrderDetails] = useState("");
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const orderId = queryParams.get("orderId");

  const currency = generalSetting?.currency || "PKR";

  function convertDateTimeToDate(datetimeString) {
    const date = new Date(datetimeString); // Parse the datetime string to a Date object
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are zero-indexed, so add 1
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`; // Return formatted date string
  }

  function formatFriendlyDateTime(datetimeString) {
    if (!datetimeString) return "";
    const date = new Date(datetimeString);
    if (isNaN(date.getTime())) return datetimeString;
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true
    });
  }

  useEffect(() => {
    axios
      .get(`${apiKey}/api/rms/customer/fetch_order/${orderId}`)
      .then((res) => {
        setOrderDetails(res.data);
      })
      .catch((err) => { })
      .finally(() => {
        setLoading(false);
      });
  }, [orderId]);

  if (loading) {
    return (
      <div className="absolute top-0 left-0 w-full h-full grid place-items-center">
        <img src="/assets/gif/dash2.gif" alt="" />
      </div>
    );
  }

  if (orderDetails?.order_type?.name === "Dine-in")
    return (
      <main>
        <section className="py-16">
          <div className="w-[90%] max-w-7xl mx-auto">
            <div>
              <img
                className="max-w-[400px] mx-auto"
                src="/assets/icon_confirmation.gif"
                alt=""
              />
            </div>
            <div className="text-center ">
              <h1 className="text-4xl font-semibold">Thank You!</h1>
              <p className="text-3xl font-medium mt-4 text-w-bg">
                Your order for {orderDetails?.table?.name} has been placed
                successfully.
              </p>
              <p className="font-bold">
                For any query please call any waiter nearer to your table.
              </p>
              <div className="flex justify-center w-fit flex-col text-w-btn-text font-bold text-lg rounded-md py-2 px-4 mt-5 border border-w-border bg-w-btn mx-auto">
                <Link to={"/"} className="flex justify-center">
                  Continue To Homepage
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
    );
  if (
    orderDetails?.order_type?.name === "Delivery" ||
    orderDetails?.order_type?.name === "Take away"
  )
    return (
      <main>
        <section className="py-16">
          <div className="max-w-xl w-full mx-auto rounded border-t-2 border-b-2 border-w-border shadow-lg">
            <div className="px-8">
              <h1 className="text-center font-semibold text-w-heading text-4xl mt-2">
                Thank You!
              </h1>
              <h2 className="text-w-bg font-semibold text-3xl mt-4">
                Order Summary
              </h2>
              <div className="grid grid-cols-2 gap-x-4 mt-8 text-gray-500 font-bold text-sm">
                <span>
                  Tracking Number:{" "}
                  <Link to={`/trackorder/${orderDetails?.sale_no}`} className="underline text-blue-600">
                    {orderDetails?.sale_no}
                  </Link>
                </span>
                <span>
                  {orderDetails?.order_type?.name === "Delivery" &&
                    "Delivery Address"}
                </span>
                <span>
                  Order Time {convertDateTimeToDate(orderDetails?.sale_date)}{" "}
                  {orderDetails?.order_time}
                </span>
                <span>
                  {orderDetails?.order_type?.name === "Delivery" &&
                    orderDetails?.is_guest === "yes"
                    ? orderDetails?.guest?.address
                    : orderDetails?.address}
                </span>
              </div>
              {(orderDetails?.preorder_datetime || orderDetails?.takeaway_time) && (
                <div className="mt-4 text-purple-900 bg-purple-50 p-3 rounded-lg border border-purple-200 flex justify-between items-center text-sm font-semibold">
                  <span>
                    {orderDetails?.order_type?.name === "Delivery"
                      ? "🚚 Scheduled Delivery Time:"
                      : "🛍️ Scheduled Pickup Time:"}
                  </span>
                  <span className="font-extrabold text-purple-950">
                    {formatFriendlyDateTime(orderDetails?.preorder_datetime || orderDetails?.takeaway_time)}
                  </span>
                </div>
              )}

              {/* Payment Summary Section */}
              <div className="mt-8 border-t border-b border-gray-200 py-4">
                <h3 className="font-semibold text-xl text-w-heading mb-3">{languageHelper("Payment Details") || "Payment Details"}</h3>
                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex justify-between">
                    <span>{languageHelper("Sub Total") || "Sub Total"}</span>
                    <span>{orderDetails?.sub_total} {currency}</span>
                  </div>
                  {Number(orderDetails?.total_discount_amount) > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>{languageHelper("Total Discount") || "Total Discount"}</span>
                      <span>-{orderDetails?.total_discount_amount} {currency}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-base border-t pt-2 text-gray-900">
                    <span>{languageHelper("Total Amount") || "Total Amount"}</span>
                    <span>{orderDetails?.total_payable} {currency}</span>
                  </div>
                  {orderDetails?.advance_payment_percentage && (
                    <div className="flex justify-between font-bold text-purple-700 bg-purple-50 p-2 rounded mt-2">
                      <span>{languageHelper("Advance Paid") || "Advance Paid"} ({orderDetails.advance_payment_percentage}%)</span>
                      <span>{orderDetails.advance_payment_amount} {currency}</span>
                    </div>
                  )}
                  {orderDetails?.advance_payment_percentage && orderDetails.advance_payment_percentage < 100 && (
                    <div className="flex justify-between text-xs text-gray-500 italic px-2">
                      <span>{languageHelper("Remaining Balance Due") || "Remaining Balance Due"}</span>
                      <span>{(orderDetails.total_payable - orderDetails.advance_payment_amount).toFixed(2)} {currency}</span>
                    </div>
                  )}
                  {orderDetails?.order_type_id === 2 && (
                    <div className="text-[11px] text-amber-700 bg-amber-50 p-2 rounded border border-amber-200 mt-3 leading-normal">
                      ⚠️ <strong>{languageHelper("Delivery Charges Note") || "Delivery Charges Note"}:</strong>{" "}
                      {languageHelper("Delivery charges are NOT included in the total. They will be added separately on confirmation based on actual InDrive or Bykea fare to your location.") ||
                        "Delivery charges are NOT included in the total. They will be added separately on confirmation based on actual InDrive or Bykea fare to your location."}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="px-8 py-6 leading-loose bg-gray-100 mt-8">
              <p>
                Hey (
                {orderDetails?.is_guest === "yes"
                  ? orderDetails?.guest?.name
                  : orderDetails?.customer?.name}
                ), Well chosen. Your order from ({generalSetting?.restaurant_name}) is on its way. If there
                is anything else you need, please head over to the Help number
                ({generalSetting?.phone}) for assistance.
              </p>
            </div>
            <div className="flex justify-evenly items-center my-2">
              <img
                className="w-16"
                src={logo}
                alt=""
              />
              <p className="text-lg">&copy; {new Date().getFullYear()} {generalSetting?.restaurant_name}</p>
            </div>

            <div className="flex gap-4 justify-evenly items-center my-6">
              <Link
                to={"/"}
                className="text-w-btn-text bg-w-btn font-medium rounded-md py-1 px-2"
              >
                Continue To Homepage
              </Link>
              <Link to={`/trackorder/${orderDetails?.sale_no}`} className="text-w-btn-text bg-w-btn font-medium rounded-md py-1 px-2">
                Track Your Order
              </Link>
            </div>
          </div>
        </section>
      </main>
    );
}

export default ThankYou;
